import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard } from "@/components/ui";
import { workspaceScopeFromActiveContext } from "@/models/aiChat";
import type { SBotAppEnvironmentSurfaceReport } from "./services/appEnvironmentContext";
import { useAppEnvironmentReport } from "./AppEnvironmentScreen";
import { parseAppDeepLink } from "./services/appNavigation";
import {
  FirestoreSBotAppNavigationResourceChecker,
  SBotAppNavigationAuthorizer,
} from "./services/appNavigationAuthorization";

export function AppNavigationDeepLink() {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const navigationIndex = useMemo(() => new FirestoreSBotAppNavigationResourceChecker(), []);
  const authorizer = useMemo(
    () => new SBotAppNavigationAuthorizer(navigationIndex),
    [navigationIndex],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const resolve = async () => {
      try {
        const origin = window.location.origin;
        const route = parseAppDeepLink(
          `${origin}${location.pathname}${location.search}${location.hash}`,
          { expectedOrigin: origin },
        );
        const authorized = await authorizer.authorize(route, {
          currentUser,
          isSBotLabsAdmin,
          workspaceScope: currentUser?.activeContext
            ? workspaceScopeFromActiveContext(currentUser.activeContext)
            : undefined,
        });
        if (!cancelled) navigate(authorized.appPath, { replace: true });
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "This SBot destination could not be opened.");
        }
      }
    };

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [
    authorizer,
    currentUser,
    isSBotLabsAdmin,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  const environmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => ({
    identity: "app-navigation-deep-link",
    owningTab: "home",
    breadcrumbs: ["App Destination"],
    destinationType: "appNavigation",
    surfaceLevel: "pushedDestination",
    surfaceDepth: 1,
    surfacePresentation: "routeDestination",
    screen: {
      title: error ? "Can’t open destination" : "Opening App Destination",
      state: error ? "error" : "resolving",
      contextCompleteness: "basic",
      sections: [{
        id: "app-navigation-current-view",
        title: "Current View",
        controls: [{
          role: "status",
          label: error ? "Destination could not be opened" : "Resolving authorized destination",
        }],
      }],
    },
  }), [error]);
  useAppEnvironmentReport(environmentReport);

  if (!error) return <LoadingView label="Opening destination…" />;

  return (
    <div className="list-screen">
      <SBCard>
        <h1>Can&rsquo;t open destination</h1>
        <ErrorText>{error}</ErrorText>
        <p>
          <Link to="/home">Return to Home</Link>
        </p>
      </SBCard>
    </div>
  );
}
